import { useCallback } from 'react';

import {
  ClickAwayListener,
  Box as Flex,
  Tooltip,
  styled,
} from '@material-ui/core';

import { RiDeleteBinLine, RiPaletteLine } from '@remixicon/react';
import type { ResolvedPos } from '@tiptap/pm/model';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';

import { CommentZoneProvider } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../../hooks';
import { SemanticColor } from '../../utils/';
import { BlockReorderButtons } from '../BlockReorderButtons';
import { ColorPicker } from '../ColorPicker';
import { BlockCommentButton } from '../Comment';
import { useCurrentEditor } from '../Context';
import { FloatingCard, ToolbarCard } from '../FloatingCard';
import { ToolbarButton } from '../ToolbarButton';
import { useDropdownGroup } from '../useDropdownGroup';

const ColorPickerContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: theme.spacing(1),
  zIndex: 9999,
}));

const ButtonContainer = styled('div')({
  position: 'relative',
});

const findCalloutBlockInfo = (state: { selection: { $from: ResolvedPos } }) => {
  const { $from } = state.selection;
  let depth = $from.depth;
  while (depth > 0) {
    if ($from.node(depth).type.name === 'calloutBlock') {
      return { pos: $from.before(depth), node: $from.node(depth) };
    }
    depth--;
  }
  return null;
};

const BlockToolbarContent = () => {
  const { editor } = useCurrentEditor();
  const {
    isOpen: isColorPickerOpen,
    toggle: toggleColorPicker,
    close: closeColorPicker,
  } = useDropdownGroup();

  const currentAttrs = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor)
        return { backgroundColor: null, borderColor: null, blockId: null };
      const info = findCalloutBlockInfo(ctx.editor.state);
      if (!info)
        return { backgroundColor: null, borderColor: null, blockId: null };
      return {
        backgroundColor: info.node.attrs.backgroundColor ?? null,
        borderColor: info.node.attrs.borderColor ?? null,
        blockId: info.node.attrs.blockId ?? null,
      };
    },
  }) ?? { backgroundColor: null, borderColor: null, blockId: null };

  const handleBackgroundColorSelect = useCallback(
    (semanticColor: SemanticColor | null) => {
      if (!editor) return;
      const info = findCalloutBlockInfo(editor.state);
      if (!info) return;
      const { pos, node } = info;
      const { tr } = editor.state;
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        backgroundColor: semanticColor,
      });
      editor.view.dispatch(tr);
      editor.commands.focus();
    },
    [editor],
  );

  const handleBorderColorSelect = useCallback(
    (semanticColor: SemanticColor | null) => {
      if (!editor) return;
      const info = findCalloutBlockInfo(editor.state);
      if (!info) return;
      const { pos, node } = info;
      const { tr } = editor.state;
      tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        borderColor: semanticColor,
      });
      editor.view.dispatch(tr);
      editor.commands.focus();
    },
    [editor],
  );

  const handleDeleteBlock = useCallback(() => {
    if (!editor) return;
    const info = findCalloutBlockInfo(editor.state);
    if (!info) return;
    const { pos, node } = info;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  }, [editor]);

  const hasAnyColor = currentAttrs.backgroundColor || currentAttrs.borderColor;
  const zoneId = useBlockZoneId('callout-block', currentAttrs.blockId);

  return (
    <ToolbarCard>
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 'var(--bui-space-0_5)',
        }}
      >
        <BlockReorderButtons />
        <ClickAwayListener onClickAway={closeColorPicker}>
          <ButtonContainer>
            <Tooltip title="Block colors">
              <ToolbarButton
                active={hasAnyColor}
                aria-label="Block colors"
                onClick={toggleColorPicker}
              >
                <RiPaletteLine />
              </ToolbarButton>
            </Tooltip>
            {isColorPickerOpen && (
              <ColorPickerContainer>
                <FloatingCard p="3" pt="2">
                  <Flex
                    display="flex"
                    flexDirection="column"
                    style={{
                      gap: 'var(--bui-space-4)',
                    }}
                  >
                    <ColorPicker
                      label="Background"
                      category="background"
                      currentValue={currentAttrs.backgroundColor}
                      onSelect={handleBackgroundColorSelect}
                      variant="filled"
                    />
                    <ColorPicker
                      label="Border"
                      category="text"
                      currentValue={currentAttrs.borderColor}
                      onSelect={handleBorderColorSelect}
                      variant="outlined"
                    />
                  </Flex>
                </FloatingCard>
              </ColorPickerContainer>
            )}
          </ButtonContainer>
        </ClickAwayListener>
        {zoneId && (
          <CommentZoneProvider zone={zoneId}>
            <BlockCommentButton />
          </CommentZoneProvider>
        )}
        <Tooltip title="Delete block">
          <ToolbarButton aria-label="Delete block" onClick={handleDeleteBlock}>
            <RiDeleteBinLine />
          </ToolbarButton>
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );
};

export const BlockToolbar = () => {
  const { editor } = useCurrentEditor();

  const getReferencedVirtualElement = useCallback(() => {
    if (!editor) return null;
    const info = findCalloutBlockInfo(editor.state);
    if (!info) return null;
    const blockDOM = editor.view.nodeDOM(info.pos) as HTMLElement;
    if (!blockDOM) return null;
    return {
      getBoundingClientRect: () => blockDOM.getBoundingClientRect(),
    };
  }, [editor]);

  return (
    <BubbleMenu
      editor={editor ?? undefined}
      pluginKey="blockToolbar"
      getReferencedVirtualElement={getReferencedVirtualElement}
      options={{ offset: 6, placement: 'top-end' }}
      style={{ zIndex: 99999 }}
      appendTo={() => document.body}
      shouldShow={({ view, state }) => {
        if (!view.hasFocus()) return false;
        return findCalloutBlockInfo(state) !== null;
      }}
    >
      <BlockToolbarContent />
    </BubbleMenu>
  );
};
